import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { useOnline, useQueueCount, syncQueue } from "../lib/offline";
import AssistantPanel from "./AssistantPanel";
import { BrandLogo, SignalMark } from "./Logo";
import {
  Sun, Moon, CalendarDays, Users, UserRound, Mic, Search, Sparkles, ShieldCheck, ListChecks, ScrollText, Gauge, Waypoints, Target, Briefcase, WifiOff, CloudUpload, LogOut, SlidersHorizontal, Building2, FileOutput, ChartColumn,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV = {
  msl: [
    { to: "/today", label: "Prepare", icon: CalendarDays, testid: "nav-today" },
    { to: "/kols", label: "KOLs", icon: Users, testid: "nav-kols" },
    { to: "/capture", label: "Capture", icon: Mic, testid: "nav-capture" },
    { to: "/memory", label: "Learn", icon: Search, testid: "nav-memory" },
    { to: "/followups", label: "Follow up", icon: ListChecks, testid: "nav-followups" },
    { to: "/impact", label: "Time back", icon: Gauge, testid: "nav-impact" },
    { to: "/uploads", label: "Uploads", icon: CloudUpload, testid: "nav-uploads" },
    { to: "/reports", label: "Reports", icon: FileOutput, testid: "nav-reports" },
  ],
  leadership: [
    { to: "/command", label: "Home", icon: Briefcase, testid: "nav-command" },
    { to: "/decisions", label: "Priorities", icon: ListChecks, testid: "nav-priorities" },
    { to: "/signals", label: "Signals", icon: Sparkles, testid: "nav-signals" },
    { to: "/opportunities", label: "Opportunities", icon: Target, testid: "nav-opportunities" },
    { to: "/graph", label: "Graph", icon: Waypoints, testid: "nav-graph" },
    { to: "/team", label: "Team", icon: UserRound, testid: "nav-team" },
    { to: "/impact", label: "Impact", icon: Gauge, testid: "nav-impact" },
    { to: "/kols", label: "KOLs", icon: Users, testid: "nav-kols" },
    { to: "/memory", label: "Memory", icon: Search, testid: "nav-memory" },
    { to: "/governance", label: "Governance", icon: SlidersHorizontal, testid: "nav-governance" },
    { to: "/uploads", label: "Uploads", icon: CloudUpload, testid: "nav-uploads-lead" },
    { to: "/reports", label: "Reports", icon: FileOutput, testid: "nav-reports-lead" },
  ],
  compliance: [
    { to: "/compliance", label: "Guardrails", icon: ShieldCheck, testid: "nav-compliance" },
    { to: "/audit", label: "Audit Trail", icon: ScrollText, testid: "nav-audit" },
    { to: "/kols", label: "KOLs", icon: Users, testid: "nav-kols" },
    { to: "/uploads", label: "Uploads", icon: CloudUpload, testid: "nav-uploads-compliance" },
    { to: "/reports", label: "Reports", icon: FileOutput, testid: "nav-reports-compliance" },
  ],
};

const ENV_TONE = {
  demo: "bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800",
  pilot: "bg-sky-100 dark:bg-sky-950/50 text-sky-800 dark:text-sky-300 border-sky-300 dark:border-sky-800",
  production: "bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800",
  development: "bg-muted text-muted-foreground border-border",
};

function WorkspaceBadge() {
  const { user } = useApp();
  const tenant = user?.tenant;
  if (!tenant) return null;
  return (
    <span data-testid="workspace-badge" className="hidden lg:inline-flex items-center gap-2 text-xs text-muted-foreground" title={`Dataset: ${tenant.dataset_id}`}>
      <span className="max-w-[160px] truncate">{tenant.name}</span>
      <span className="sr-only"> · </span>
      <span className={`border rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${ENV_TONE[tenant.environment] || ENV_TONE.development}`} data-testid="environment-pill">
        {tenant.environment}
      </span>
    </span>
  );
}

function ViewToggle() {
  const { role, switchView, switchPersona, isDemo } = useApp();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(null);
  const views = [
    ["msl", "MSL", "/today"],
    ["leadership", "Medical Affairs", "/command"],
    ["compliance", "Compliance", "/compliance"],
  ];
  const pick = async (key, home) => {
    if (key === role || switching) return;
    if (isDemo) {
      setSwitching(key);
      try {
        await switchPersona(key);
        navigate(home);
      } catch {
        toast.error("Could not switch persona — try again");
      } finally {
        setSwitching(null);
      }
    } else {
      switchView(key);
      navigate(home);
    }
  };
  return (
    <div className="flex rounded-full border border-border p-0.5 bg-muted/40" data-testid="view-toggle">
      {views.map(([key, label, home]) => (
        <button
          key={key}
          data-testid={`view-${key}`}
          aria-pressed={role === key}
          disabled={!!switching}
          onClick={() => pick(key, home)}
          className={`px-2.5 sm:px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-60 ${
            role === key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {switching === key ? "…" : label}
        </button>
      ))}
    </div>
  );
}

function RoleThemeControls() {
  const { logout, dark, setDark, roleInfo } = useApp();
  const navigate = useNavigate();
  const signOut = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="flex items-center gap-2">
      <button
        data-testid="theme-toggle"
        onClick={() => setDark(!dark)}
        className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted transition-colors"
        aria-label="Toggle theme"
      >
        {dark ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu" className="flex items-center gap-2 rounded-full border border-border pl-1 pr-3 py-1 hover:bg-muted transition-colors">
            <span className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-semibold">
              {(roleInfo.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
            </span>
            <span className="text-sm font-medium hidden sm:block">{roleInfo.label}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <span className="block">{roleInfo.name}</span>
            <span className="block text-xs font-normal text-muted-foreground">{roleInfo.label}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="logout-btn" onClick={signOut} className="text-destructive focus:text-destructive">
            <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ConnectivityPill() {
  const online = useOnline();
  const queued = useQueueCount();
  const wasOffline = useRef(false);
  const syncing = useRef(false);

  useEffect(() => {
    if (!online) { wasOffline.current = true; return; }
    if (wasOffline.current && queued > 0 && !syncing.current) {
      syncing.current = true;
      toast.info("Back online — syncing offline captures…");
      syncQueue().then(({ synced, remaining }) => {
        syncing.current = false;
        if (synced > 0) toast.success(`${synced} offline capture${synced > 1 ? "s" : ""} synced & saved`);
        if (remaining > 0) toast(`${remaining} capture${remaining > 1 ? "s" : ""} need a quick review in Capture`);
      });
      wasOffline.current = false;
    }
  }, [online, queued]);

  if (online && queued === 0) return null;
  return (
    <span
      data-testid="connectivity-pill"
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 ${
        !online
          ? "bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300"
          : "bg-accent text-accent-foreground"
      }`}
    >
      {!online ? <WifiOff className="h-3.5 w-3.5" strokeWidth={1.5} /> : <CloudUpload className="h-3.5 w-3.5" strokeWidth={1.5} />}
      {!online ? "Offline — captures saved locally" : `${queued} pending sync`}
    </span>
  );
}

export default function Layout({ children }) {
  const { role, user } = useApp();
  const nav = user?.is_admin
    ? [...NAV[role],
       { to: "/admin", label: "Admin", icon: Building2, testid: "nav-admin" },
       { to: "/admin/analytics", label: "Analytics", icon: ChartColumn, testid: "nav-admin-analytics" }]
    : NAV[role];
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border bg-card/40 px-5 py-7 z-30">
        <button onClick={() => navigate(role === "leadership" ? "/command" : role === "compliance" ? "/compliance" : "/today")} data-testid="brand" className="mb-10">
          <BrandLogo tagline="Intelligence Platform" />
        </button>
        <nav className="flex flex-col gap-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/admin"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-muted"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto text-xs text-muted-foreground leading-relaxed">
          <span className="italic text-sm text-foreground/70">Prepare. Meet. Capture. Learn.</span>
          <p className="mt-2">{user?.tenant?.environment === "demo" ? "Diabetes & Obesity · India" : user?.tenant?.name || ""}</p>
        </div>
      </aside>

      {/* Top bar */}
      <header className="fixed top-0 right-0 left-0 md:left-64 h-16 border-b border-border bg-background/80 backdrop-blur-xl z-20 flex items-center justify-between px-5 md:px-8">
        <div className="md:hidden flex items-center gap-2">
          <SignalMark size={30} />
          <span className="font-semibold tracking-tight text-lg hidden sm:block">MA Signals</span>
        </div>
        <div className="hidden md:block" />
        <div className="flex items-center gap-2 sm:gap-3">
          <WorkspaceBadge />
          <ConnectivityPill />
          <ViewToggle />
          <RoleThemeControls />
        </div>
      </header>

      {/* Main — animated page transitions */}
      <main className="md:pl-64 pt-16 pb-24 md:pb-10">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.2, 0.6, 0.3, 1] }}
          className="mx-auto max-w-6xl px-5 md:px-10 py-8"
        >
          {children}
        </motion.div>
      </main>

      {/* Chief of staff — works exclusively for the MSL */}
      {role === "msl" && <AssistantPanel />}

      {/* Mobile bottom nav — one-handed reach, ≥44px targets, safe-area aware */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 border-t border-border bg-background/90 backdrop-blur-md z-30 flex items-stretch justify-around px-2 pb-safe">
        {nav.slice(0, 6).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`m-${n.testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 flex-1 min-h-[68px] min-w-[44px] text-[10px] transition-colors ${
                isActive ? "text-primary font-semibold" : "text-muted-foreground"
              }`
            }
          >
            {n.label === "Capture" ? (
              <span className="h-12 w-12 -mt-4 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-lg">
                <n.icon className="h-5 w-5" strokeWidth={1.5} />
              </span>
            ) : (
              <n.icon className="h-5 w-5" strokeWidth={1.5} />
            )}
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
